/**
 * Response mappers for the TPIN-first family (ADR-005 v1.6 §3). Raw DTOs end
 * here: stores, components and pages only ever see `@domain/tpin` types.
 *
 * No arithmetic beyond the contract invariant check
 * (`reachable + unreachable = total`), and no status derivation (ADR-000 F2).
 */

import { ApiError } from '@domain/types'
import type { TpinBranch, TpinBranchPage, TpinDirectoryPage, TpinPeriodSummary } from '@domain/tpin'
import { assertNumber, assertObject, assertOptionalString, assertString } from './mappers'

const TIN_RE = /^[0-9]+$/
const BRANCH_DB_RE = /^[0-9]+_[0-9]+$/

function assertArray(value: unknown, what: string): readonly unknown[] {
  if (!Array.isArray(value)) {
    throw new ApiError(502, 'INVALID_RESPONSE', `BFF field ${what} is missing or not an array.`)
  }
  return value
}

/** GET /api/agg/tpins — one directory page; rows carry exactly `tin`. */
export function mapDirectoryPage(payload: unknown): TpinDirectoryPage {
  const root = assertObject(payload, 'the TPIN directory')
  const customers = assertArray(root['customers'], 'customers').map((entry) => {
    const tin = assertString(assertObject(entry, 'a directory row')['tin'], 'tin')
    if (!TIN_RE.test(tin)) {
      throw new ApiError(502, 'INVALID_RESPONSE', `BFF TPIN "${tin}" is not numeric.`)
    }
    return { tin }
  })
  return {
    customers,
    nextCursor: assertOptionalString(root['nextCursor']),
    hasMore: root['hasMore'] === true,
  }
}

/** GET /api/agg/tpins/{tin}/summary — coverage-qualified counts. */
export function mapTpinSummary(payload: unknown): TpinPeriodSummary {
  const root = assertObject(payload, 'the TPIN summary')
  const summary: TpinPeriodSummary = {
    tin: assertString(root['tin'], 'tin'),
    synced: assertNumber(root['synced'], 'synced'),
    failed: assertNumber(root['failed'], 'failed'),
    pending: assertNumber(root['pending'], 'pending'),
    freshness: assertOptionalString(root['freshness']),
    branchesTotal: assertNumber(root['branches_total'], 'branches_total'),
    branchesReachable: assertNumber(root['branches_reachable'], 'branches_reachable'),
    branchesUnreachable: assertNumber(root['branches_unreachable'], 'branches_unreachable'),
  }
  if (summary.branchesReachable + summary.branchesUnreachable !== summary.branchesTotal) {
    throw new ApiError(502, 'INVALID_RESPONSE', 'BFF coverage counts break reachable + unreachable = total.')
  }
  return summary
}

function mapBranch(entry: unknown): TpinBranch {
  const item = assertObject(entry, 'a branch row')
  const dbName = assertString(item['dbName'], 'dbName')
  if (!BRANCH_DB_RE.test(dbName)) {
    throw new ApiError(502, 'INVALID_RESPONSE', `BFF dbName "${dbName}" is not in {TPIN}_{branch} form.`)
  }
  if (item['unreachable'] === true) {
    return { kind: 'unreachable', dbName, reason: assertOptionalString(item['unreachableReason']) }
  }
  return {
    kind: 'reachable',
    dbName,
    synced: assertNumber(item['synced'], 'synced'),
    failed: assertNumber(item['failed'], 'failed'),
    pending: assertNumber(item['pending'], 'pending'),
    freshness: assertOptionalString(item['freshness']),
  }
}

/** GET /api/agg/tpins/{tin}/branches — one branch page. */
export function mapBranchPage(payload: unknown): TpinBranchPage {
  const root = assertObject(payload, 'the branch page')
  return {
    tin: assertString(root['tin'], 'tin'),
    branches: assertArray(root['branches'], 'branches').map(mapBranch),
    nextCursor: assertOptionalString(root['nextCursor']),
    hasMore: root['hasMore'] === true,
  }
}
